// Shadow Network — Search page (traders + analyses).
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Search } from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";
import { tShadow } from "../i18n/shadow-social";
import { ProfileCard } from "../components/shadow/ProfileCard";

import API from "../lib/api";

const ShadowSearchPage = () => {
  const { lang } = useLanguage();
  const [query, setQuery] = useState("");
  const [tab, setTab] = useState("traders"); // traders | analyses
  const [discover, setDiscover] = useState([]);
  const [loading, setLoading] = useState(true);

  // Load discover feed for analyses tab + use the leaderboard for traders suggestions
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const [feedRes, leaderRes] = await Promise.all([
          axios.get(`${API}/shadow/social/feed?type=discover&limit=24`),
          axios.get(`${API}/shadow/leaderboard?limit=12`).catch(() => ({ data: [] })),
        ]);
        if (!mounted) return;
        setDiscover(feedRes.data?.items || []);
        // Leaderboard items become trader suggestions (best-effort hydration).
        const lb = leaderRes.data?.items || leaderRes.data || [];
        if (Array.isArray(lb) && lb.length) {
          // Best-effort: map shape of leaderboard to ProfileCard shape.
          setSuggestions(
            lb.slice(0, 9).map((p) => ({
              user: {
                id: p.user_id || p.id,
                name: p.name || p.codename || "Operator",
                username: p.username,
                avatar_url: p.avatar_url || p.picture,
                is_verified: p.is_verified,
              },
              stats: {
                total_analyses: p.total_analyses || p.analyses_count || 0,
                win_rate: p.win_rate || 0,
              },
              badges: [],
              followers_count: p.followers_count || 0,
            }))
          );
        }
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  const [suggestions, setSuggestions] = useState([]);

  const q = query.trim().toLowerCase();
  const filteredTraders = useMemo(
    () =>
      !q
        ? suggestions
        : suggestions.filter((p) => {
            const u = p.user || {};
            return (
              (u.name || "").toLowerCase().includes(q) ||
              (u.username || "").toLowerCase().includes(q)
            );
          }),
    [suggestions, q]
  );
  const filteredAnalyses = useMemo(
    () =>
      !q
        ? discover
        : discover.filter((a) =>
            (a.title || "").toLowerCase().includes(q) ||
            (a.ticker || "").toLowerCase().includes(q) ||
            (a.description || "").toLowerCase().includes(q)
          ),
    [discover, q]
  );

  return (
    <div className="min-h-screen bg-black text-white" data-testid="search-page">
      <header className="border-b border-white/5 bg-black/70 backdrop-blur-xl sticky top-0 z-30">
        <div className="mx-auto flex max-w-5xl items-center justify-between gap-4 px-4 py-4 md:px-8">
          <Link
            to="/shadow"
            className="inline-flex items-center gap-2 text-white/60 transition-colors hover:text-white"
          >
            <ArrowLeft className="h-4 w-4" />
            <span className="text-xs font-bold uppercase tracking-[0.2em]">Shadow</span>
          </Link>
          <h1 className="text-base font-bold uppercase tracking-[0.18em]">
            {{ es: "Buscar", en: "Search", sk: "Hľadať", de: "Suchen", "zh-CN": "搜索" }[lang] || "Buscar"}
          </h1>
          <span className="w-20" />
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-4 py-8 md:px-8">
        {/* Search bar */}
        <div className="relative mb-6">
          <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-white/40" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={tShadow("buscarTraders", lang)}
            data-testid="search-input"
            className="w-full rounded-full border border-white/10 bg-black/30 py-3 pl-11 pr-4 text-sm text-white placeholder:text-white/40 focus:border-amber-400/40 focus:outline-none"
          />
        </div>

        {/* Tabs */}
        <div className="mb-6 flex items-center gap-2">
          {[
            { id: "traders", label: { es: "Traders", en: "Traders", sk: "Obchodníci", de: "Trader", "zh-CN": "交易者" }[lang] || "Traders" },
            { id: "analyses", label: tShadow("analisis", lang) },
          ].map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`search-tab-${t.id}`}
              className={`rounded-full border px-4 py-1.5 text-xs font-bold uppercase tracking-[0.18em] transition-all ${
                tab === t.id
                  ? "border-amber-400/60 bg-amber-400/[0.12] text-amber-200"
                  : "border-white/10 bg-white/[0.03] text-white/55 hover:text-white"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Suggestions header */}
        {!query && tab === "traders" && (
          <h2 className="mb-4 text-[10px] font-bold uppercase tracking-[0.25em] text-white/40">
            {tShadow("sugerencias", lang)}
          </h2>
        )}

        {loading ? (
          <p className="text-center text-white/40 py-12">…</p>
        ) : tab === "traders" ? (
          filteredTraders.length === 0 ? (
            <p className="text-center text-white/40 py-12">—</p>
          ) : (
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              {filteredTraders.map((p) => (
                <ProfileCard key={p.user.id} profile={p} />
              ))}
            </div>
          )
        ) : filteredAnalyses.length === 0 ? (
          <p className="text-center text-white/40 py-12">—</p>
        ) : (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
            {filteredAnalyses.map((a) => (
              <Link
                key={a.id}
                to={`/shadow#analysis-${a.id}`}
                className="block overflow-hidden rounded-2xl border border-white/10 bg-white/[0.03] hover:border-amber-400/40"
              >
                {(a.image_url || a.video_url) && (
                  <div className="aspect-video w-full overflow-hidden bg-black">
                    <img src={a.image_url || a.video_url} alt="" className="h-full w-full object-cover" />
                  </div>
                )}
                <div className="p-4">
                  <p className="line-clamp-2 text-sm font-semibold text-white">{a.title}</p>
                  <p className="mt-1 text-[10px] uppercase tracking-[0.15em] text-white/40">
                    {a.ticker || "—"} · {a.direction || "—"}
                  </p>
                </div>
              </Link>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default ShadowSearchPage;
